import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SignIn from './SignIn';
import SignUp from './SignUp';
import { isAuthenticated } from '../../utils/authUtils';
import { useLanguage } from '../../utils/LanguageContext';
import BrandLogo from '../layout/BrandLogo';
import { LanguageToggle, ColorModeToggle } from '../layout/AppShell';

const text = {
  en: {
    referral: 'You were invited by a friend! Sign up to get a free 3-day Planning Pass.',
    continueGuest: 'Continue without an account',
    by: 'by Kondingau Community Insight',
  },
  my: {
    referral: 'Anda dijemput oleh rakan! Daftar untuk dapat Pas Perancangan 3 hari percuma.',
    continueGuest: 'Teruskan tanpa akaun',
    by: 'oleh Kondingau Community Insight',
  },
};

// Only allow returning to pages inside the app
const safeRedirect = (next) => (next && next.startsWith('/app') ? next : '/app');

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const t = text[language] || text.en;
  const [isSignIn, setIsSignIn] = useState(searchParams.get('mode') !== 'signup');
  const redirectTo = safeRedirect(searchParams.get('next'));
  const hasReferral = !!localStorage.getItem('rumahkira_referral_code');

  useEffect(() => {
    if (isAuthenticated()) navigate(redirectTo, { replace: true });
  }, [navigate, redirectTo]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate(redirectTo)} sx={{ color: 'text.secondary' }}>
          RumahKira
        </Button>
        <Stack direction="row" spacing={1}>
          <LanguageToggle />
          <ColorModeToggle />
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, pb: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <BrandLogo size={40} sx={{ justifyContent: 'center' }} />
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
            {t.by}
          </Typography>
        </Box>

        {hasReferral && (
          <Card sx={{ width: '100%', maxWidth: 420, mb: 2, borderColor: 'success.main' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CardGiftcardRoundedIcon color="success" />
              <Typography variant="body2" fontWeight={500}>
                {t.referral}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Card sx={{ width: '100%', maxWidth: 420 }}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            {isSignIn ? (
              <SignIn onToggleForm={() => setIsSignIn(false)} redirectTo={redirectTo} />
            ) : (
              <SignUp onToggleForm={() => setIsSignIn(true)} redirectTo={redirectTo} />
            )}
          </CardContent>
        </Card>

        <Button onClick={() => navigate(redirectTo)} sx={{ mt: 2, color: 'text.secondary' }}>
          {t.continueGuest}
        </Button>
      </Box>
    </Box>
  );
};

export default AuthPage;
